import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';

import TextArea from 'components/Inputs/TextArea';
import Button from 'kingpin/atoms/Button';
import Icon from 'kingpin/atoms/Icon';
import Typography from 'kingpin/atoms/Typography';
import Loading from 'components/Loading/Loading';
import Tooltip from 'components/Tooltip';
import FleetOpsStaffOnly from 'components/Common/FleetOpsStaffOnly';
import { InsightsChatContext } from 'contextProviders/InsightsChatProvider';
import { InsightsEntityType } from 'hooks/useCurrentInsightsEntity';
import useMetricOptions from 'hooks/useMetricOptions';

import describeAttachedView from './describeAttachedView';
import getStarterQuestions from './getStarterQuestions';
import InsightsAnswerBody from './InsightsAnswerBody';
import InsightsStudioEdits from './InsightsStudioEdits';
import InsightsCopyButton from './InsightsCopyButton';
import InsightsLiveReasoning from './InsightsLiveReasoning';
import InsightsMessageFeedback from './InsightsMessageFeedback';
import InsightsMessageNav from './InsightsMessageNav';
import { stripCtxFooter } from './stripCtxFooter';
import SuggestionChips from './SuggestionChips';

// Fills the available height of its parent (which must be a flex column) so the
// transcript scrolls and the composer stays pinned to the bottom.
const Root = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

// Wraps the scrolling transcript so the scroll-to-bottom button can float over
// its bottom edge without being clipped by the transcript's own overflow.
const TranscriptWrap = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const Transcript = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-right: 4px;
`;

// Floats over the bottom of the transcript, centred, and only appears when the
// user has scrolled up away from the latest message. Clicking jumps to the end.
const ScrollToBottomButton = styled.button`
  position: absolute;
  bottom: 8px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 999px;
  border: 1px solid var(--color-border-secondary);
  background-color: var(--color-bg-secondary);
  box-shadow: var(--shadow-floating-button);
  cursor: pointer;
  transition: background-color 0.12s ease;

  &:hover {
    background-color: var(--color-bg-tertiary);
  }
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 8px;
  color: var(--color-text-secondary);
`;

const Bubble = styled.div<{ $role: 'user' | 'assistant' }>`
  max-width: 85%;
  padding: 10px 14px;
  border-radius: 12px;
  align-self: ${(props) =>
    props.$role === 'user' ? 'flex-end' : 'flex-start'};
  background-color: ${(props) =>
    props.$role === 'user'
      ? 'var(--color-bg-tertiary)'
      : 'var(--color-bg-secondary)'};
  border: 1px solid var(--color-border-secondary);
`;

const LoadingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--color-text-secondary);
`;

// Lays the copy button beside a message bubble (not below it) so the copy
// affordance adds no vertical height — preserving the transcript's spacing and
// the FailedIndicator "hug" under a question. Full width so the bubble's
// max-width stays relative to the transcript, not to its own content.
const UserTurn = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
`;

const AssistantBubbleRow = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
`;

// Reveals the copy button only while its message row is hovered (or keyboard
// focused). Kept in the DOM — opacity, not display — so it stays clickable and
// the layout never shifts.
const HoverCopy = styled.div`
  opacity: 0;
  transition: opacity 0.12s ease;

  ${UserTurn}:hover &,
  ${UserTurn}:focus-within &,
  ${AssistantBubbleRow}:hover &,
  ${AssistantBubbleRow}:focus-within & {
    opacity: 1;
  }
`;

// An assistant turn: the persistent reasoning ghost stream stacked above the
// answer (or the loading spinner) bubble. Full width so the inner bubble's
// max-width is relative to the transcript; children left-align (assistant side).
const AssistantGroup = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`;

// A failed answer, rendered attached under the user's question bubble it
// belongs to (right-aligned, messaging-app style) instead of floating as its
// own assistant bubble: warning icon + optional actionable detail + Retry.
const FailedIndicator = styled.div`
  align-self: flex-end;
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 85%;
  /* Hug the failed question above: pull back most of the transcript's gap. */
  margin-top: -8px;
`;

const Composer = styled.div`
  margin-top: 8px;
`;

// The send button floats inside the input at the bottom-right, so the textarea
// is the positioning context and reserves padding on the right for it.
const ComposerInputWrap = styled.div`
  position: relative;
`;

const ComposerInput = styled(TextArea)`
  padding-right: 44px;

  &:focus {
    border: 1px solid var(--color-ai-accent);
    box-shadow: 0px 0px 0px 2px var(--color-ai-accent-subtle);
  }
`;

const SendButtonEl = styled.button`
  position: absolute;
  right: 8px;
  bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  border: none;
  background-color: var(--color-ai-accent);
  cursor: pointer;
  transition: background-color 0.12s ease;

  &:hover:not(:disabled) {
    background-color: var(--color-ai-accent-hover);
  }

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`;

// The hint on the right; the "pick a value to explain" control (when the
// screen has something to pick from) on the left.
const ComposerFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 6px;
`;

const ComposerHint = styled.div`
  margin-left: auto;
  color: var(--color-text-secondary);
  text-align: right;
`;

// "Focused on Dallas · MPG by Terminal ×": the value the conversation is
// pinned to (see InsightsChatContext.activeFocus). Same footprint as the
// entity badge, tinted with the AI accent so it reads as chat state.
const FocusChip = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  max-width: 100%;
  margin-top: 8px;
  padding: 4px 8px;
  border-radius: 8px;
  border: 1px solid var(--color-ai-accent);
  background-color: var(--color-ai-accent-subtle);
`;

// Shown while the picker is armed, so the user knows the next click on the
// report is being captured rather than filtering it.
const PickerBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 8px;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px dashed var(--color-ai-accent);
  background-color: var(--color-ai-accent-subtle);
`;

const SuggestedEyebrow = styled.div`
  margin-bottom: 6px;
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const EmptyStateChips = styled.div`
  width: 100%;
  margin-top: 8px;
`;

const FollowUps = styled.div`
  margin-top: 8px;
`;

const EntityBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  max-width: 100%;
  margin-top: 12px;
  padding: 4px 8px;
  border-radius: 8px;
  border: 1px solid var(--color-border-secondary);
  background-color: var(--color-bg-secondary);
`;

const EntityBadgeLabel = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DismissButton = styled.button`
  display: flex;
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
`;

const ENTITY_ICON: Record<InsightsEntityType, Kingpin.Icon> = {
  report: 'chart-outlined',
  dashboard: 'dashboard',
  workspace: 'workspace',
};

// The main conversational area shared by the slideout and the full-page chat:
// the empty state with starter prompts, the streaming transcript, the
// current-content badge and the composer. All state comes from
// InsightsChatContext, so both hosts render the same live conversation. The
// parent must be a flex column that gives this component height (the slideout
// Container / the page's main column).
const InsightsChatConversation = () => {
  const {
    messages,
    isLoading,
    sendQuestion,
    retryLastFailed,
    activeEntity,
    dismissEntity,
    selectedWorkspace,
    activeFocus,
    clearFocus,
    explainPicker,
  } = useContext(InsightsChatContext);
  const { metricOptions, metricOptionsNoSpecials } = useMetricOptions();
  const [draft, setDraft] = useState<string>('');

  // A metric DISPLAY NAME is safe to put in a starter chip only when the agent
  // can resolve it to exactly one metric: it must be findable (normal/compound,
  // not a special metric absent from find_metrics) and unique across the catalog
  // (a name shared by several metrics — "Total Miles" on different datasets —
  // is ambiguous). Otherwise tapping the chip lands on a clarifying question.
  const isMetricAnswerable = useMemo(() => {
    const nameCounts = new Map<string, number>();
    metricOptions.forEach((m) => {
      if (m.name) {
        nameCounts.set(m.name, (nameCounts.get(m.name) ?? 0) + 1);
      }
    });
    const findableNames = new Set(
      metricOptionsNoSpecials
        .map((m) => m.name)
        .filter((name): name is string => Boolean(name)),
    );
    return (name: string) =>
      findableNames.has(name) && nameCounts.get(name) === 1;
  }, [metricOptions, metricOptionsNoSpecials]);

  // What the agent will be told about the current screen. Reports and dashboards
  // both publish a view; this is absent only when neither is on screen (e.g. a
  // workspace or a popup/headless render).
  const attachedView = useMemo(
    () => (activeEntity?.view ? describeAttachedView(activeEntity.view) : null),
    [activeEntity],
  );

  const lastMessageId = messages[messages.length - 1]?.id;

  // Don't auto-follow new content — let the user read at their own pace. Instead
  // track whether they're scrolled to the bottom so we can offer a jump-to-latest
  // button when new messages land off-screen.
  const transcriptRef = useRef<HTMLDivElement | null>(null);
  const [isAtBottom, setIsAtBottom] = useState<boolean>(true);

  // Land at the bottom (newest message) once, when the transcript first mounts —
  // i.e. opening the chat or loading a conversation from history. A callback ref
  // fires only on mount/unmount, so this never re-runs as later content streams
  // in: the initial jump happens, then we stop following.
  const setTranscriptRef = useCallback((node: HTMLDivElement | null) => {
    transcriptRef.current = node;
    if (node) {
      node.scrollTop = node.scrollHeight;
    }
  }, []);

  const updateIsAtBottom = useCallback(() => {
    const el = transcriptRef.current;
    if (!el) {
      return;
    }
    // Small tolerance so sub-pixel rounding doesn't leave the button stuck on.
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setIsAtBottom(distanceFromBottom <= 8);
  }, []);

  // Re-evaluate when the content height changes (new messages, streaming tokens)
  // so the button appears if fresh content pushes the bottom out of view.
  useEffect(() => {
    updateIsAtBottom();
  }, [messages, updateIsAtBottom]);

  const scrollToBottom = useCallback(() => {
    const el = transcriptRef.current;
    // Feature-detect scrollTo (absent in jsdom) so this is safe under test.
    if (el && typeof el.scrollTo === 'function') {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }
  }, []);

  // The user's questions, for the Conductor-style top-right nav (one bar each).
  const userMessages = useMemo(
    () =>
      messages
        .filter((m) => m.role === 'user')
        .map((m) => ({ id: m.id, content: m.content })),
    [messages],
  );

  // DOM nodes for user-message bubbles, so the nav can scroll to one. A callback
  // ref keeps the map in sync as messages mount/unmount.
  const messageNodes = useRef<Map<string, HTMLDivElement>>(new Map());
  const registerMessageNode = useCallback(
    (id: string) => (node: HTMLDivElement | null) => {
      if (node) {
        messageNodes.current.set(id, node);
      } else {
        messageNodes.current.delete(id);
      }
    },
    [],
  );

  const scrollToMessage = useCallback((id: string) => {
    const el = transcriptRef.current;
    const node = messageNodes.current.get(id);
    if (!el || !node || typeof el.scrollTo !== 'function') {
      return;
    }
    // Position within the scroll container (getBoundingClientRect is robust to
    // the transcript not being the bubble's offsetParent). A small top margin
    // keeps the message from butting against the very top edge.
    const top =
      node.getBoundingClientRect().top -
      el.getBoundingClientRect().top +
      el.scrollTop;
    el.scrollTo({ top: Math.max(0, top - 8), behavior: 'smooth' });
  }, []);

  // Auto-scroll to the bottom when the user sends a new question — but only for
  // a freshly appended user message, not for assistant streaming tokens (those
  // keep the read-at-your-own-pace behavior above). Covers every send path
  // uniformly (composer, starter chips, follow-up chips) since each appends a
  // `user` message. The ref is seeded on first run so the mount-time jump in
  // setTranscriptRef isn't duplicated with a redundant smooth-scroll.
  const lastUserMessageIdRef = useRef<string | undefined>(undefined);
  const isFirstScrollSyncRef = useRef<boolean>(true);
  useEffect(() => {
    let latestUserMessageId: string | undefined;
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].role === 'user') {
        latestUserMessageId = messages[i].id;
        break;
      }
    }

    if (isFirstScrollSyncRef.current) {
      isFirstScrollSyncRef.current = false;
      lastUserMessageIdRef.current = latestUserMessageId;
      return;
    }

    if (
      latestUserMessageId &&
      latestUserMessageId !== lastUserMessageIdRef.current
    ) {
      lastUserMessageIdRef.current = latestUserMessageId;
      scrollToBottom();
    }
  }, [messages, scrollToBottom]);

  const onSend = () => {
    if (!draft.trim() || isLoading) {
      return;
    }
    sendQuestion(draft);
    setDraft('');
  };

  // Empty when nothing on screen anchors a chip — see getStarterQuestions. The
  // invitation above them is hidden too: "try one of these" with nothing under
  // it is worse than a plain empty state.
  const starterQuestions = useMemo(
    () =>
      getStarterQuestions({
        entity: activeEntity,
        workspaceName: selectedWorkspace?.label,
        isMetricAnswerable,
      }),
    [activeEntity, selectedWorkspace?.label, isMetricAnswerable],
  );

  return (
    <Root>
      {messages.length === 0 ? (
        <EmptyState>
          <Typography.Body type="Body 13 Bold">
            Ask a question about your data
          </Typography.Body>
          {starterQuestions.length > 0 && (
            <>
              <Typography.Body type="Body 12">
                {activeEntity
                  ? `Suggestions for ${activeEntity.entityName}`
                  : 'Try one of these to get started'}
              </Typography.Body>
              <EmptyStateChips>
                <SuggestionChips
                  questions={starterQuestions}
                  onSelect={sendQuestion}
                  isDisabled={isLoading}
                />
              </EmptyStateChips>
            </>
          )}
        </EmptyState>
      ) : (
        <TranscriptWrap>
          <Transcript ref={setTranscriptRef} onScroll={updateIsAtBottom}>
            {messages.map((message) => (
              <React.Fragment key={message.id}>
                {message.role === 'assistant' && message.isError ? (
                  <FailedIndicator data-testid="insights-message-error">
                    <Icon
                      icon="warning"
                      width={16}
                      height={16}
                      color="var(--color-status-danger)"
                    />
                    <Typography.Body
                      type="Body 12"
                      color="var(--color-status-danger)"
                    >
                      Message failed to send
                    </Typography.Body>
                    {message.errorMessage && (
                      <Typography.Body type="Body 12">
                        {message.errorMessage}
                      </Typography.Body>
                    )}
                    {message.id === lastMessageId && (
                      <Button
                        size="Small"
                        type="Ghost"
                        icon="refresh"
                        label="Retry"
                        onClick={retryLastFailed}
                        isDisabled={isLoading}
                        testId="insights-retry-button"
                      />
                    )}
                  </FailedIndicator>
                ) : message.role === 'assistant' ? (
                  <AssistantGroup>
                    {/* Staff-only reasoning ghosts, kept above the answer both
                      while it streams and after it lands (not collapsed into a
                      footer) — every thought/tool accumulates as its own ghost,
                      nothing is overwritten. Empty for non-staff (server-gated);
                      the wrapper also hides it while impersonating. */}
                    <FleetOpsStaffOnly>
                      <InsightsLiveReasoning trace={message.trace} />
                    </FleetOpsStaffOnly>
                    <AssistantBubbleRow>
                      <Bubble $role={message.role}>
                        {message.isLoading ? (
                          <>
                            <LoadingRow>
                              <Loading isButton />
                              {message.toolStatus && (
                                <Typography.Body type="Body 12">
                                  {message.toolStatus}
                                </Typography.Body>
                              )}
                            </LoadingRow>
                            {/* Studio edits land mid-answer, as they're applied */}
                            <InsightsStudioEdits edits={message.studioEdits} />
                          </>
                        ) : (
                          <InsightsAnswerBody message={message} />
                        )}
                      </Bubble>
                      {!message.isLoading && (
                        <HoverCopy>
                          <InsightsCopyButton
                            text={stripCtxFooter(message.content)}
                            testId="insights-copy-assistant"
                          />
                        </HoverCopy>
                      )}
                    </AssistantBubbleRow>
                    {!message.isLoading && (
                      <InsightsMessageFeedback
                        messageId={message.id}
                        feedback={message.feedback}
                        sources={message.sources}
                      />
                    )}
                  </AssistantGroup>
                ) : (
                  <UserTurn>
                    <HoverCopy>
                      <InsightsCopyButton
                        text={message.content}
                        testId="insights-copy-user"
                      />
                    </HoverCopy>
                    <Bubble
                      $role={message.role}
                      ref={registerMessageNode(message.id)}
                    >
                      <Typography.Body type="Body 13">
                        {message.content}
                      </Typography.Body>
                    </Bubble>
                  </UserTurn>
                )}
                {message.id === lastMessageId &&
                  !message.isLoading &&
                  !!message.suggestions?.length &&
                  (() => {
                    // No dedicated clarify signal on the wire (kept lightweight):
                    // infer it. A clarifying turn asks ONE question and runs no
                    // aggregation, so its answer ends in '?' with no chart/source.
                    // Then the chips are the ANSWERS to that question, not
                    // scattered follow-ups — label and style them as choices.
                    const isClarify =
                      !message.charts?.length &&
                      !message.sources?.length &&
                      message.content.trim().endsWith('?');
                    return (
                      <FollowUps>
                        <SuggestedEyebrow>
                          <Typography.Body type="Body 12">
                            {isClarify ? 'Pick one to continue' : 'Suggested'}
                          </Typography.Body>
                        </SuggestedEyebrow>
                        <SuggestionChips
                          questions={message.suggestions}
                          onSelect={sendQuestion}
                          isDisabled={isLoading}
                          variant={isClarify ? 'answer' : 'suggestion'}
                        />
                      </FollowUps>
                    );
                  })()}
              </React.Fragment>
            ))}
          </Transcript>
          <InsightsMessageNav
            messages={userMessages}
            onSelect={scrollToMessage}
          />
          {!isAtBottom && (
            <ScrollToBottomButton
              type="button"
              onClick={scrollToBottom}
              aria-label="Scroll to latest"
              data-testid="insights-scroll-to-bottom"
            >
              <Icon
                icon="arrow-down"
                width={16}
                height={16}
                color="var(--color-text-secondary)"
              />
            </ScrollToBottomButton>
          )}
        </TranscriptWrap>
      )}

      {activeEntity && (
        <EntityBadge data-testid="insights-entity-badge">
          <Icon
            icon={ENTITY_ICON[activeEntity.entityType]}
            width={14}
            height={14}
            color="var(--color-text-secondary)"
          />
          <EntityBadgeLabel>
            <Typography.Body type="Body 12">
              {activeEntity.entityName}
            </Typography.Body>
            {attachedView && (
              // The agent is being sent this report's date range, filter values
              // and on-screen figures. Show that plainly rather than leaving the
              // user to guess what left the page.
              <Tooltip
                content={
                  <div>
                    {attachedView.detail.map((line) => (
                      <div key={line}>
                        <Typography.Body
                          type="Body 12"
                          color="var(--color-tooltip-text)"
                        >
                          {line}
                        </Typography.Body>
                      </div>
                    ))}
                  </div>
                }
              >
                <Typography.Body
                  type="Body 12"
                  color="var(--color-text-secondary)"
                >
                  {attachedView.summary}
                </Typography.Body>
              </Tooltip>
            )}
          </EntityBadgeLabel>
          <DismissButton
            type="button"
            onClick={dismissEntity}
            aria-label="Ask about all your data"
          >
            <Icon
              icon="cross"
              width={12}
              height={12}
              color="var(--color-text-secondary)"
            />
          </DismissButton>
        </EntityBadge>
      )}

      {activeFocus && (
        <FocusChip data-testid="insights-focus-chip">
          <Icon
            icon="sparkles"
            width={14}
            height={14}
            color="var(--color-ai-accent)"
          />
          <EntityBadgeLabel>
            <Typography.Body type="Body 12">
              {activeFocus.value
                ? `Focused on ${activeFocus.value.label} · ${activeFocus.gadgetTitle}`
                : `Focused on ${activeFocus.gadgetTitle}`}
            </Typography.Body>
          </EntityBadgeLabel>
          <DismissButton
            type="button"
            onClick={clearFocus}
            aria-label="Clear focus"
            data-testid="insights-focus-clear"
          >
            <Icon
              icon="cross"
              width={12}
              height={12}
              color="var(--color-text-secondary)"
            />
          </DismissButton>
        </FocusChip>
      )}

      {explainPicker.isArmed && (
        <PickerBanner data-testid="insights-explain-picker-banner">
          <Typography.Body type="Body 12">
            {`Click a value on the ${
              activeEntity?.entityType ?? 'report'
            } to explain it. Press Esc to cancel.`}
          </Typography.Body>
          <Button
            size="Small"
            type="Ghost"
            label="Cancel"
            onClick={explainPicker.disarm}
            testId="insights-explain-picker-cancel"
          />
        </PickerBanner>
      )}

      <Composer>
        <ComposerInputWrap>
          <ComposerInput
            placeholder="Ask about your fleet data…"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                onSend();
              }
            }}
          />
          <SendButtonEl
            type="button"
            onClick={onSend}
            disabled={!draft.trim()}
            data-testid="insights-send"
            aria-label="Send"
          >
            <Icon
              icon="arrow-up"
              width={16}
              height={16}
              color="var(--color-text-inverse)"
            />
          </SendButtonEl>
        </ComposerInputWrap>
      </Composer>
      <ComposerFooter>
        {explainPicker.isAvailable && !explainPicker.isArmed && (
          <Button
            size="Small"
            type="Ghost"
            icon="sparkles"
            label="Pick a value to explain"
            onClick={explainPicker.arm}
            isDisabled={isLoading}
            testId="insights-explain-picker-arm"
          />
        )}
        <ComposerHint>
          <Typography.Body type="Body 12">
            Enter to send · Shift+Enter for newline
          </Typography.Body>
        </ComposerHint>
      </ComposerFooter>
    </Root>
  );
};

export default InsightsChatConversation;
